import React, { useState, useEffect, useRef } from 'react';
import { Route, Switch, useParams, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainContent from './Coach/MainContent';
import CoachHeaderCard from './Coach/CoachHeaderCard';
import NotFound from './Shared/NotFound';
import API, { Profile, Session, Package, Event, FacilityAPIResponse, transformCoachesResponse, transformLessonRatesToSessions, Coach, transformStripeProductToPackage, ProgramAPI, transformProgramToEvent } from './services/api';
import { schedulingFetchGet } from 'app/utils/request/scheduling/fetch';
import { fetchGet } from 'app/utils/request';
import settings from 'config/environment';
import facilityBackgroundImage from './assets/facility-top-background.png';
import coachNowLogo from './assets/coachnow-small-logo.png';
import './styles.scss';

// Helper function to format address from components
const formatAddress = (address?: string, city?: string, country?: string, zip?: string): string | undefined => {
  const parts = [address, city, country && zip ? `${country} ${zip}` : country || zip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : undefined;
};

// Helper function to transform Coach data to Profile format
const transformCoachToProfile = (
  coach: Coach,
  facilityName?: string,
  facilityId?: string | number,
  facilitySlug?: string,
  facilityLogo?: string,
  facilityBackgroundImage?: string,
  phone?: string,
  email?: string,
  website?: string,
  about?: string,
  avatarUrl?: string,
  awards?: string,
  currency?: string
): Profile => {
  return {
    username: coach.username,
    firstName: coach.name.split(' ')[0] || '',
    lastName: coach.name.split(' ').slice(1).join(' ') || '',
    role: coach.role,
    timeZone: coach.timeZone,
    avatar: avatarUrl || coach.avatar || '',
    about: about || coach.about || '',
    contact: {
      phone: phone || coach.phone || '',
      email: email || coach.email || '',
      website: website || coach.website || ''
    },
    locations: facilityName ? [{
      id: 1,
      name: facilityName,
      address: coach.location,
      coordinates: { lat: 0, lon: 0 }
    }] : [],
    backgroundImage: undefined,
    facility: (facilityId && facilityName && facilitySlug) ? {
      id: typeof facilityId === 'string' ? parseInt(facilityId, 10) : facilityId,
      name: facilityName,
      slug: facilitySlug,
      logo: facilityLogo,
      backgroundImage: facilityBackgroundImage
    } : undefined,
    awards: awards || '',
    currency: currency || coach.currency
  };
};

const PublicCoachProfile: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { facilitySlug, coachExternalId } = useParams<{ facilitySlug: string; coachName: string; coachExternalId: string }>();

  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [notFound, setNotFound] = useState(false);
  const [publicCalendarUrl, setPublicCalendarUrl] = useState<string | undefined>(undefined);
  const [publicProgramsUrl, setPublicProgramsUrl] = useState<string | undefined>(undefined);
  const [coachPublicLandingPage, setCoachPublicLandingPage] = useState<boolean | null>(null);
  const history = useHistory();

  const hasAppliedCoachProfile = useRef(false);
  const coachNowProfile = null;

  const loadStripeProducts = async (coachExternalId: string) => {
    try {
      const stripeProducts = await schedulingFetchGet(`stripe_products`, { coach_id: coachExternalId, include_offline: true }, {}, undefined, true);

      // Transform and filter packages to only show enabled ones
      const transformedPackages = stripeProducts
        .map(transformStripeProductToPackage)
        .filter(pkg => pkg.enabled === true);

      setPackages(transformedPackages);
    } catch (error) {
      console.error('Error loading stripe products:', error);
      // Set empty array on error so UI doesn't break
      setPackages([]);
    }
  };

  useEffect(() => {
    loadStripeProducts(coachExternalId);
  }, [coachExternalId]);

  const loadAllData = async (coachExternalIdParam: string, facilitySlugParam: string | undefined, contactInfo?: { phone?: string; email?: string; website?: string; about?: string; avatarUrl?: string; coverUrl?: string; awards?: string }) => {
    try {
      // Check if we have coach data from navigation state (optimization)
      const stateCoach = (location.state as any)?.coach as Coach | undefined;
      const stateFacilityId = (location.state as any)?.facilityId as string | undefined;
      const stateFacilityName = (location.state as any)?.facilityName as string | undefined;
      const stateFacilitySlug = (location.state as any)?.facilitySlug as string | undefined;
      const stateFacilityLogo = (location.state as any)?.facilityLogo as string | undefined;
      const stateFacilityBackgroundImage = (location.state as any)?.facilityBackgroundImage as string | undefined;
      const stateLessonRates = (location.state as any)?.lessonRates as any[] | undefined;
      const stateEvents = (location.state as any)?.events as Event[] | undefined;
      const statePublicCalendarUrl = (location.state as any)?.publicCalendarUrl as string | undefined;
      const statePublicProgramsUrl = (location.state as any)?.publicProgramsUrl as string | undefined;

      let coachProfile: Profile;
      let coachUsername: string;
      let transformedSessions: Session[] = [];
      let finalPublicCalendarUrl: string | undefined = statePublicCalendarUrl;
      let finalPublicProgramsUrl: string | undefined = statePublicProgramsUrl;
      let coachPublicLanding = false;

      if (stateCoach && stateCoach.id === coachExternalIdParam && stateFacilityId) {
        // Use coach data from navigation state (fast path - no API call needed if we have lessonRates)
        coachProfile = transformCoachToProfile(
          stateCoach,
          stateFacilityName || stateCoach.location,
          stateFacilityId,
          stateFacilitySlug,
          stateFacilityLogo,
          stateFacilityBackgroundImage,
          contactInfo?.phone,
          contactInfo?.email,
          contactInfo?.website,
          contactInfo?.about,
          contactInfo?.avatarUrl,
          stateCoach.awards || contactInfo?.awards
        );

        // Set address from stateCoach if available
        if (stateCoach.address) {
          coachProfile.address = stateCoach.address;
        }

        coachUsername = stateCoach.username;
        coachPublicLanding = stateCoach.publicLandingPage || false;
        setBackgroundImage(contactInfo?.coverUrl || stateCoach?.coverUrl || stateFacilityBackgroundImage || facilityBackgroundImage);

        // Use lesson rates from navigation state if available, otherwise fetch
        if (stateLessonRates && stateLessonRates.length >= 0) {
          // Transform lesson rates from navigation state (no API call!)
          transformedSessions = transformLessonRatesToSessions(stateLessonRates);
        } else {
          // Fallback: fetch coach profile directly to get lesson rates
          try {
            const coachResponse = await schedulingFetchGet(`coach_profiles/${coachExternalId}/landing_page_coach`, {}, {}, undefined, true);

            transformedSessions = coachResponse.lessonRates
              ? transformLessonRatesToSessions(coachResponse.lessonRates)
              : [];
          } catch (error) {
            console.error('Error loading coach profile:', error);
            history.push('/');
            return;
          }
        }
      } else if (stateFacilityId) {
        try {
          // Fetch coach profile directly from scheduling API
          const coachResponse = await schedulingFetchGet(`coach_profiles/${coachExternalId}/landing_page_coach`, {}, {}, undefined, true);

          // Fetch facility data for facility-specific info (name, logo, cover, etc.)
          const facilityResponse = await schedulingFetchGet<FacilityAPIResponse>(
            `facilities/${stateFacilityId}`,
            {}, {}, undefined, true
          );
          // Handle the response as a single object (not an array)
          const facilityData = Array.isArray(facilityResponse) ? facilityResponse[0] : facilityResponse;

          // Transform coach response to Coach format for compatibility
          const coaches = transformCoachesResponse(
            [coachResponse],
            facilityData.name
          );

          const foundCoach = coaches[0];

          if (!foundCoach) {
            history.push('/');
            throw new Error('Coach not found');
          }

          coachProfile = transformCoachToProfile(
            foundCoach,
            facilityData.name,
            facilityData.id,
            facilityData.uuid,
            facilityData.publicLogoUrl || undefined,
            facilityData.publicCoverUrl || undefined,
            contactInfo?.phone,
            contactInfo?.email,
            contactInfo?.website,
            contactInfo?.about,
            contactInfo?.avatarUrl,
            foundCoach.awards || contactInfo?.awards
          );
          coachUsername = foundCoach.username;
          coachPublicLanding = foundCoach.publicLandingPage || false;

          // Transform lesson rates to sessions from coach response
          transformedSessions = coachResponse.lessonRates
            ? transformLessonRatesToSessions(coachResponse.lessonRates)
            : [];

          setBackgroundImage(contactInfo?.coverUrl || foundCoach?.coverUrl || facilityData.publicCoverUrl || facilityBackgroundImage);
        } catch (error) {
          console.error('Error loading coach profile:', error);
          history.push('/');
          return;
        }
      } else if (facilitySlugParam) {
        // Fallback: Use facilitySlug from URL to fetch all data (for direct URL access)

        try {
          // Fetch coach profile directly from scheduling API (includes facility data)
          const coachResponse = await schedulingFetchGet(`coach_profiles/${coachExternalId}/landing_page_coach`, {}, {}, undefined, true);

          // Transform coach response to Coach format for compatibility
          const coaches = transformCoachesResponse(
            [coachResponse],
            coachResponse.facility?.name || ''
          );

          const foundCoach = coaches[0];

          if (!foundCoach) {
            throw new Error('Coach not found');
          }

          // Construct public URLs for buttons
          finalPublicCalendarUrl = `${settings.PUBLIC_URL}/public/availability?coach=${foundCoach.calendarUuid}`;
          finalPublicProgramsUrl = coachResponse.facility?.publicProgramsUrl;

          // Create initial coach profile
          coachProfile = transformCoachToProfile(
            foundCoach,
            coachResponse.facility?.name || '',
            coachResponse.facility?.id,
            coachResponse.facility?.uuid,
            coachResponse.facility?.publicLogoUrl || undefined,
            coachResponse.facility?.publicCoverUrl || undefined,
            contactInfo?.phone,
            contactInfo?.email,
            contactInfo?.website,
            contactInfo?.about,
            contactInfo?.avatarUrl,
            foundCoach.awards || contactInfo?.awards
          );
          coachUsername = foundCoach.username;
          coachPublicLanding = foundCoach.publicLandingPage || false;

          // Transform lesson rates to sessions from coach response
          transformedSessions = coachResponse.lessonRates
            ? transformLessonRatesToSessions(coachResponse.lessonRates)
            : [];

          // Variable to store coach cover URL from organization API
          let coachCoverUrl: string | undefined;

        // Second API call: Fetch additional facility info from organization API
        try {
          const coachProfileResponse = await fetchGet(
            `coach_profiles/${coachExternalId}`,
            {clientId: settings.API_CLIENT_ID},
            {},
            undefined,
            true
          );

          // Merge coach profile data from organization API
          if (coachProfileResponse) {
            // Extract coach cover URL from organization API
            coachCoverUrl = coachProfileResponse.coverUrl;

            // Extract and format address components
            const formattedAddress = formatAddress(
              coachProfileResponse.address,
              coachProfileResponse.city,
              coachProfileResponse.country,
              coachProfileResponse.zip
            );

            // Update coach profile with organization data
            coachProfile = {
              ...coachProfile,
              about: coachProfileResponse.about || coachProfile.about,
              contact: {
                phone: coachProfileResponse.phone || coachProfile.contact.phone,
                email: coachProfileResponse.email || coachProfile.contact.email,
                website: coachProfileResponse.website || coachProfile.contact.website
              },
              awards: coachProfileResponse.awards || coachProfile.awards,
              address: formattedAddress
            };
          }

          // Use public logo and cover from organization API if available
          setBackgroundImage(
            contactInfo?.coverUrl ||
            coachCoverUrl ||
            coachResponse.facility?.publicCoverUrl ||
            facilityBackgroundImage
          );
          } catch (error) {
            console.error('Error loading additional facility data:', error);
            // Continue with basic facility data
            setBackgroundImage(contactInfo?.coverUrl || coachCoverUrl || coachResponse.facility?.publicCoverUrl || facilityBackgroundImage);
          }
        } catch (error) {
          console.error('Error loading coach profile:', error);
          history.push('/');
          return;
        }
      } else {
        // No facility ID or slug available - cannot load coach data
        throw new Error('Facility information not available');
      }

      // Load packages and events (use state if available, otherwise fetch)
      let events: Event[] = [];

      if (stateEvents) {
        // Use events from navigation state (no API call!)
        events = stateEvents;
      } else if (facilitySlugParam) {
        // Fallback: load events from programs API when accessed directly via URL
        try {
          const programsResponse = await schedulingFetchGet(
            '/programs',
            { facility_id: facilitySlugParam, registration_status: 'open_now_or_in_future' },
            {},
            undefined,
            true
          );

          const programsData = programsResponse?.data?.active || [];
          const facilityName = coachProfile.facility?.name || '';
          events = programsData.map((program: ProgramAPI) =>
            transformProgramToEvent(program, facilityName)
          );
        } catch (error) {
          console.error('Error loading programs:', error);
          events = [];
        }
      } else {
        // Final fallback: use getEvents API
        try {
          const eventsRes = await API.getEvents(coachUsername);
          events = eventsRes.data.events;
        } catch (error) {
          console.error('Error loading events:', error);
          events = [];
        }
      }

      /**
       * The address, and so the map, used to come only from the organization
       * API's coach profile — and that endpoint answers 404 for a coach who
       * exists in scheduling but not there, which is how a facility with a
       * perfectly good address ended up with no map at all.
       *
       * The scheduling facility payload carries `fullAddress` and always
       * answers, so it stands in when the other has nothing. Asked for only in
       * that case, so the usual path makes no extra request.
       */
      if (!coachProfile.address) {
        const facilityUuid =
          facilitySlugParam || coachProfile.facility?.slug || undefined;

        if (facilityUuid) {
          try {
            const facilityForAddress = await schedulingFetchGet<FacilityAPIResponse>(
              `facilities/${facilityUuid}`,
              {}, {}, undefined, true
            );
            const facilityRecord = Array.isArray(facilityForAddress)
              ? facilityForAddress[0]
              : facilityForAddress;

            if (facilityRecord?.fullAddress) {
              coachProfile = { ...coachProfile, address: facilityRecord.fullAddress };
            }
          } catch {
            // A missing address is a missing map, not a broken page.
          }
        }
      }

      setProfile(coachProfile);
      setSessions(transformedSessions);
      setEvents(events);
      setLoading(false);
      setUsername(coachUsername);
      setNotFound(false);
      setPublicCalendarUrl(finalPublicCalendarUrl);
      setPublicProgramsUrl(finalPublicProgramsUrl);
      setCoachPublicLandingPage(coachPublicLanding);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error instanceof Error && error.message && error.message.includes('not found')) {
        setLoading(false);
        setNotFound(true);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const currentCoachExternalId = coachExternalId || 'alina-carpenter';
    window.scrollTo(0, 0);
    setLoading(true);
    const contactInfo = coachNowProfile ? {
      phone: coachNowProfile.phone,
      email: coachNowProfile.email,
      website: coachNowProfile.website,
      about: coachNowProfile.about,
      avatarUrl: coachNowProfile.avatarUrl,
      coverUrl: coachNowProfile.coverUrl,
      awards: coachNowProfile.awards
    } : undefined;
    loadAllData(currentCoachExternalId, facilitySlug, contactInfo);
  }, [coachExternalId, facilitySlug, coachNowProfile]);

  // Update profile when coachNowProfile data becomes available
  useEffect(() => {
    if (coachNowProfile && profile && !hasAppliedCoachProfile.current) {
      if (coachNowProfile.about || coachNowProfile.phone || coachNowProfile.email || coachNowProfile.website || coachNowProfile.avatarUrl || coachNowProfile.coverUrl) {
        setProfile({
          ...profile,
          avatar: coachNowProfile.avatarUrl || profile.avatar,
          about: coachNowProfile.about || profile.about,
          contact: {
            phone: coachNowProfile.phone || profile.contact.phone,
            email: coachNowProfile.email || profile.contact.email,
            website: coachNowProfile.website || profile.contact.website
          }
        });
        if (coachNowProfile.coverUrl) {
          setBackgroundImage(coachNowProfile.coverUrl);
        }
        hasAppliedCoachProfile.current = true;
      }
    }
  }, [coachNowProfile, profile]);

  // Reset ref when coachExternalId changes
  useEffect(() => {
    hasAppliedCoachProfile.current = false;
  }, [coachExternalId]);

  // Redirect to home if coach public landing page is disabled
  useEffect(() => {
    if (!loading && coachPublicLandingPage === false && username) {
      history.push('/');
    }
  }, [loading, coachPublicLandingPage, username, history]);

  const hasHeader = backgroundImage;

  if (notFound) {
    return (
      <div className="public-profiles-wrapper">
        <NotFound />
      </div>
    );
  }

  return (
    <div className="public-profiles-wrapper">
      {username && !loading ? (
        <>
          {hasHeader && (
            <div className="pub-header">
              <img
                src={backgroundImage!}
                alt="Golf Course"
                className="pub-header-image"
              />
              {profile && profile.facility && coachPublicLandingPage && (
                <div className="pub-facility-info-overlay">
                  <div className="pub-facility-info-container">
                    {/*
                      No facility name or logo over the picture: the banner carries
                      the facility itself, and the frames put nothing on it
                      (518:14976). The link to the facility lives in the sidebar's
                      Locations card, which names the same place.
                    */}
                    <div className="pub-header-actions">
                      <a href="https://coachnow.io" target="_blank" rel="noopener noreferrer" className="pub-powered-by pub-powered-by-mobile">
                        {t('Powered by')} <img src={coachNowLogo} alt="CoachNow" className="pub-coachnow-logo" />
                      </a>
                    </div>
                  </div>
                </div>
              )}
              <div className="pub-header-overlay">
                <div className="pub-header-actions">
                  <a href="https://coachnow.io" target="_blank" rel="noopener noreferrer" className="pub-powered-by pub-powered-by-desktop">
                    {t('Powered by')} <img src={coachNowLogo} alt="CoachNow" className="pub-coachnow-logo" />
                  </a>
                  {/* A visitor who already has an account needs a way in, and
                      this banner is the only chrome the page has (43593:28399). */}
                  <a href="/signin" className="pub-header-signin">
                    {t('Sign In')}
                  </a>
                </div>
              </div>
            </div>
          )}
          <div className={`pub-app-container ${!hasHeader ? 'pub-no-header' : ''}`}>
            <CoachHeaderCard profile={profile} loading={loading} />
            <Switch>
              <Route exact path="/facility/:facilitySlug/coach/:coachName/:coachExternalId" render={() => <MainContent username={username} coachExternalId={coachExternalId} profile={profile} sessions={sessions} packages={packages} events={events} publicCalendarUrl={publicCalendarUrl} publicProgramsUrl={publicProgramsUrl} />} />
              <Route exact path="/facility/:facilitySlug/coach/:coachName/:coachExternalId/private" render={() => <MainContent username={username} coachExternalId={coachExternalId} profile={profile} sessions={sessions} packages={packages} events={events} publicCalendarUrl={publicCalendarUrl} publicProgramsUrl={publicProgramsUrl} />} />
              <Route exact path="/facility/:facilitySlug/coach/:coachName/:coachExternalId/packages" render={() => <MainContent username={username} coachExternalId={coachExternalId} profile={profile} sessions={sessions} packages={packages} events={events} publicCalendarUrl={publicCalendarUrl} publicProgramsUrl={publicProgramsUrl} />} />
              <Route exact path="/facility/:facilitySlug/coach/:coachName/:coachExternalId/events" render={() => <MainContent username={username} coachExternalId={coachExternalId} profile={profile} sessions={sessions} packages={packages} events={events} publicCalendarUrl={publicCalendarUrl} publicProgramsUrl={publicProgramsUrl} />} />
            </Switch>
          </div>
        </>
      ) : (
        <>
          <div className="pub-header-placeholder"></div>
          <div className="pub-app-container">
            <CoachHeaderCard profile={null} loading />
            <MainContent username={null} coachExternalId={null} />
          </div>
        </>
      )}
    </div>
  );
};

export default PublicCoachProfile;
